'use client';
/* Built In Imports */
'use client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

/* External Imports */
import {
  Box,
  Button,
  Center,
  Checkbox,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { useMediaQuery } from 'react-responsive';

/* Internal Imports */
/* Components */
import CalendarTabsCard from '@app-ps/Components/Card/CalendarTabsCard';
import StructuredTextParser from '@app-ps/Utility/StructuredTextParser';

/* Services */
import { fetchUpcomingListData } from '@Services/projSamskritiService';

// Helper function to group cards by filterDate
const groupByDate = cards => {
  return cards?.reduce((acc, card) => {
    const date = card.filterDate;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(card);
    return acc;
  }, {});
};

// Helper function to format the date

const formatDate = dateString => {
  const date = new Date(dateString);

  const options = {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  };

  // Get the parts of the date
  const { weekday, day, month, year } = new Intl.DateTimeFormat(
    'en-GB',
    options
  )
    .formatToParts(date)
    .reduce(
      (acc, part) => {
        if (part.type in acc) {
          acc[part.type] =
            part.type === 'day' ? parseInt(part.value) : part.value;
        }
        return acc;
      },
      { weekday: '', day: '', month: '', year: '' }
    );

  // Format with commas
  return `${weekday}, ${day} ${month} ${year}`;
};

const CalendarTabs = ({
  sectionContent,
  id,
  isProduction,
  selTab,
  catType,
  region,
  lang,
}) => {
  // console.log(sectionContent,"datbase")
  // const [workshopDetails, setWorkshopDetails] = useState('');
  const isMobile = useMediaQuery({ maxWidth: '767px' });
  const router = useRouter();
  const path = usePathname();
  const searchParams = useSearchParams();
  const [fetchId, setFetchId] = useState(id);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({});
  const [extractedObjects, setExtractedObjects] = useState([]);
  const [groupedCards, setGroupedCards] = useState([]);
  const [emptyMessage, setEmptyMessage] = useState(null);
  const count = () => (isMobile ? 5 : 20);
  const [loadedCount, setLoadedCount] = useState(count());
  const [totalResults, setTotalResults] = useState(0);
  // console.log(count(), "type")

  useEffect(() => {
    setQuery({
      type: catType,
      isonline: searchParams.get('isonline') ? '1' : '',
      isfree: searchParams.get('isfree') ? '1' : '',
    });
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        let upcomingWorkShope = await fetchUpcomingListData(
          fetchId,
          searchParams.get('isfree'),
          searchParams.get('isonline'),
          count(),
          extractedObjects?.length
        );
        // console.log('upcomingWorkShope', upcomingWorkShope);
        setLoading(true);
        if (upcomingWorkShope) {
          setExtractedObjects([
            ...extractedObjects,
            ...upcomingWorkShope.extracted,
          ]);
        }
        setTotalResults(upcomingWorkShope?.resultcount);

        setLoading(false);
      } catch (err) {
        console.error('err', err);
        setLoading(false);
      }
    })();
  }, [searchParams, fetchId, loadedCount]);

  useEffect(() => {
    setGroupedCards(groupByDate(extractedObjects));
    if (!loading && extractedObjects.length === 0) {
      setEmptyMessage(sectionContent?.scheduleEmptyMessage);
    }
    // console.log('extractedObjects', extractedObjects.length)
  }, [extractedObjects]);

  // Group cards by filterDate
  const handleLoadMore = () => {
    if (loadedCount < totalResults) {
      setLoadedCount(loadedCount + count());
    }
  };

  const handleChange = e => {
    const pathName = path.split('?')[0];
    e.preventDefault();
    // setIsOnline(e?.target.checked);
    query.isfree = query.isfree === '1' ? '1' : '';
    query.isonline = e?.target.checked ? '1' : '';
    query.type = catType ? catType : '';

    const queryString = new URLSearchParams(query).toString();

    // Construct the new URL with updated query parameters.
    const newUrl = `${pathName}?${queryString}`;

    router.push(newUrl, { scroll: false });
  };

  const handleFree = e => {
    const pathName = path.split('?')[0];
    e.preventDefault();
    // setIsFree(e?.target.checked);
    query.isfree = e.target.checked ? '1' : '';
    query.isonline = query.isonline ? '1' : '';
    query.type = catType ? catType : '';
    const queryString = new URLSearchParams(query).toString();

    // Construct the new URL with updated query parameters.
    const newUrl = `${pathName}?${queryString}`;

    router.push(newUrl, { scroll: false });
  };

  return (
    <Box>
      <Box>
        <Stack
          direction={{ md: 'row', base: 'column' }}
          color="#fff"
          alignItems={{ base: 'baseline' }}
          gap={{ base: '.7rem', md: '15px' }}
          borderBottom="1px solid rgba(241, 237, 233, 0.2)"
          fontFamily="var(--FedraSansStd-book)"
          pb="24px"
        >
          <Checkbox
            onChange={e => handleFree(e)}
            iconColor="#60ADFF"
            colorScheme="black"
            isChecked={query.isfree ? true : false}
          >
            Show Only Free
          </Checkbox>

          <Checkbox
            onChange={e => handleChange(e)}
            iconColor="#60ADFF"
            colorScheme="blue"
            isChecked={query.isonline ? true : false}
          >
            Show Only Online
          </Checkbox>
        </Stack>
      </Box>

      {groupedCards &&
        Object.keys(groupedCards).length > 0 &&
        Object.entries(groupedCards).map(([date, cards]) => (
          <Box key={date}>
            {/* Display the filter date */}
            <Text
              fontWeight={{ lg: '400', base: '350' }}
              fontFamily={'var(--FedraSansStd-book)'}
              letterSpacing={'0.32px'}
              textAlign={{ base: 'left' }}
              marginTop={{ base: '2rem', lg: '3.5rem' }}
              opacity={'0.6'}
              color={'#F1EDE9'}
              fontSize={{ base: '1.5rem', md: '2.25rem' }}
              lineHeight={{ base: '2.4rem' }}
            >
              {formatDate(date)}
            </Text>
            {cards.map((card, index) => {
              const {
                address,
                samskriti,
                title,
                filterDate,
                button_label,
                amount,
                date,
                program_url,
                time,
                category_tags,
                is_online,
                currency,
                program_id,
                type,
                country,
                description,
                isOnline,
                heroSection,
                urlAlias,
              } = card;
              return (
                <CalendarTabsCard
                  key={index}
                  sectionContent={sectionContent}
                  id={id}
                  program_url={program_url}
                  isProduction={isProduction}
                  samskriti={samskriti}
                  title={title}
                  filterDate={filterDate}
                  button_label={button_label}
                  amount={amount}
                  program_id={program_id}
                  date={date}
                  category_tags={category_tags}
                  isOnline={isOnline}
                  description={description}
                  time={time}
                  is_online={is_online}
                  currency={currency}
                  address={address}
                  heroSection={heroSection}
                  type={type}
                  country={country}
                  urlAlias={urlAlias}
                  selTab={selTab}
                  pType="calendar"
                  region={region}
                  lang={lang}
                />
              );
            })}
          </Box>
        ))}
      {!loading &&
        (!groupedCards || Object.keys(groupedCards).length === 0) && (
          <Center>
            <StructuredTextParser
              color="#fff"
              textAlign="center"
              fontFamily="var(--FedraSansStd-book)"
              str={render(emptyMessage)}
            />
          </Center>
        )}
      {loading && (
        <Center py="2.5rem">
          <Spinner color="#FEFAF6" />
        </Center>
      )}
      {loadedCount < totalResults && (
        <Center>
          <Button
            variant="ghost"
            color="#F18128"
            border="2px solid #F18128"
            fontFamily="var(--FedraSansStd-book)"
            _hover={{
              bg: 'none',
              color: '#F18128',
              border: '2px solid #F18128',
            }}
            _focus={{
              bg: 'none',
              color: '#F18128',
              border: '2px solid #F18128',
            }}
            _active={{
              bg: 'none',
              color: '#F18128',
              border: '2px solid #F18128',
            }}
            py="12px"
            my={'24px'}
            fontSize="1rem"
            onClick={() => handleLoadMore()}
          >
            Load More
          </Button>
        </Center>
      )}
    </Box>
  );
};

export default CalendarTabs;
